import {LinkedListNode} from './LinkedListNode';

export class LinkedList<T> {
  private headNode: LinkedListNode<T> | null = null;
  private countNodes: number = 0;

  public static fromArray<T>(array: T[]): LinkedList<T> {
    const newList = new LinkedList<T>();
    array.forEach((value) => newList.insertLast(value));
    return newList;
  }

  public insertFirst(value: T | LinkedListNode<T>): LinkedListNode<T> {
    const newNode = value instanceof LinkedListNode ? value : new LinkedListNode(value);

    if (this.headNode) {
      newNode.setNext(this.headNode);
      this.headNode = newNode;
    } else {
      this.headNode = newNode;
    }

    this.countNodes++;
    return newNode;
  }

  public insertLast(value: T | LinkedListNode<T>): LinkedListNode<T> {
    const newNode = value instanceof LinkedListNode ? value : new LinkedListNode(value);
    if (!this.headNode) {
      this.headNode = newNode;
    } else {
      let current = this.headNode;
      while (current.hasNext()) {
        current = current.getNext()!;
      }
      current.setNext(newNode);
    }
    this.countNodes++;
    return newNode;
  }

  public insertAt(position: number, value: T | LinkedListNode<T>): LinkedListNode<T> {
    if (position < 0 || position > this.countNodes) {
      throw new Error('Position out of bounds');
    }
    if (position === 0) {
      return this.insertFirst(value);
    }
    const newNode = value instanceof LinkedListNode ? value : new LinkedListNode(value);
    let current = this.headNode;
    let previous: LinkedListNode<T> | null = null;
    let index = 0;

    while (current && index < position) {
      previous = current;
      current = current.getNext();
      index++;
    }

    if (previous) {
      previous.setNext(newNode);
      newNode.setNext(current!);
    }

    this.countNodes++;

    return newNode;
  }

  public removeFirst(): LinkedListNode<T> | null {
    if (!this.headNode) return null;
    const removedNode = this.headNode;
    this.headNode = this.headNode.getNext();
    this.countNodes--;
    return removedNode;
  }

  public removeLast(): LinkedListNode<T> | null {
    if (!this.headNode) return null;
    if (!this.headNode.hasNext()) {
      const removedNode = this.headNode;
      this.headNode = null;
      this.countNodes--;
      return removedNode;
    }
    let current = this.headNode;
    let previous: LinkedListNode<T> | null = null;

    while (current.hasNext()) {
      previous = current;
      current = current.getNext()!;
    }

    if (previous) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      previous.setNext(null);
    }

    this.countNodes--;

    return current;
  }

  public removeEach(cb: (node: LinkedListNode<T>, position: number) => boolean): number {
    let current = this.headNode;
    let previous: LinkedListNode<T> | null = null;
    let index = 0;
    let removedCount = 0;

    while (current) {
      if (cb(current, index)) {
        if (previous) {
          previous.setNext(current.getNext()!);
        } else {
          this.headNode = current.getNext();
        }
        removedCount++;
        this.countNodes--;
      } else {
        previous = current;
      }
      current = current.getNext();
      index++;
    }
    return removedCount;
  }

  public removeAt(position: number): LinkedListNode<T> | null {
    if (position < 0 || position >= this.countNodes) {
      throw new Error('Position out of bounds');
    }
    if (position === 0) {
      return this.removeFirst();
    }

    let current = this.headNode;
    let previous: LinkedListNode<T> | null = null;
    let index = 0;

    while (current && index < position) {
      previous = current;
      current = current.getNext();
      index++;
    }

    if (previous && current) {
      previous.setNext(current.getNext()!);
      this.countNodes--;
      return current;
    }
    return null;
  }

  public forEach(cb: (node: LinkedListNode<T>, position: number) => void): void {
    let current = this.headNode;
    let index = 0;
    while (current) {
      cb(current, index);
      current = current.getNext();
      index++;
    }
  }

  public find(
    cb: (node: LinkedListNode<T>) => boolean,
    startingNode?: LinkedListNode<T>,
  ): LinkedListNode<T> | undefined {
    let current = startingNode || this.headNode;

    while (current) {
      if (cb(current)) {
        return current;
      }
      current = current.getNext();
    }

    return undefined;
  }

  public findIndex(cb: (node: LinkedListNode<T>) => boolean, startingNode?: LinkedListNode<T>): number {
    let current = startingNode || this.headNode;
    let counter = 0;

    while (current) {
      if (cb(current)) {
        return counter;
      }
      current = current.getNext();
      counter++;
    }

    return -1;
  }

  public filter(cb: (node: LinkedListNode<T>, position: number) => boolean): LinkedList<T> {
    const filteredList = new LinkedList<T>();
    this.forEach((node, position) => {
      if (cb(node, position)) {
        filteredList.insertLast(node.clone());
      }
    });
    return filteredList;
  }

  public head(): LinkedListNode<T> | null {
    return this.headNode;
  }

  public count(): number {
    return this.countNodes;
  }

  public toArray(): LinkedListNode<T>[] {
    const array: LinkedListNode<T>[] = [];
    this.forEach((node) => array.push(node.clone()));
    return array;
  }

  public isEmpty(): boolean {
    return this.countNodes === 0;
  }

  public clear(): void {
    this.headNode = null;
    this.countNodes = 0;
  }
}
